import { useCallback, useEffect, useState } from 'react';
import useTranslation from 'v2/layouts/OrganizationWorkspaceLayout/components/AircraftTableSection/hooks/useTranslation';
import type { DroneResponse } from 'v2/layouts/OrganizationWorkspaceLayout/components/AircraftTableSection/types';

import useAircraft from 'hooks/useAircraft';
import useToast from 'hooks/useToast';

const useAircraftTable = () => {
    const { deleteAircraft, fetchAircrafts } = useAircraft();
    const { broadcastToast } = useToast();
    const { DELETE_ERROR, DELETE_SUCCESS, LOAD_ERROR } = useTranslation();

    const [aircraft, setAircraft] = useState<DroneResponse[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const loadPage = useCallback(
        async (pageToken?: string | null) => {
            const response = await fetchAircrafts(pageToken);

            setAircraft(currentAircraft =>
                pageToken ? [...currentAircraft, ...response.objects] : response.objects
            );
            setHasNextPage(response.hasNext);
            setNextPageToken(response.nextPageToken ?? null);
        },
        [fetchAircrafts]
    );

    useEffect(() => {
        const loadFirstPage = async () => {
            try {
                await loadPage();
            } catch {
                broadcastToast({ text: LOAD_ERROR, type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        void loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isFetchingMore) {
            return;
        }

        setIsFetchingMore(true);

        try {
            await loadPage(nextPageToken);
        } catch {
            broadcastToast({ text: LOAD_ERROR, type: 'error' });
        } finally {
            setIsFetchingMore(false);
        }
    }, [LOAD_ERROR, broadcastToast, hasNextPage, isFetchingMore, loadPage, nextPageToken]);

    const handleDelete = useCallback(
        async (aircraftId: string) => {
            try {
                await deleteAircraft(aircraftId);
            } catch {
                broadcastToast({ text: DELETE_ERROR, type: 'error' });

                return;
            }

            setAircraft(currentAircraft =>
                currentAircraft.filter(aircraftItem => aircraftItem.id !== aircraftId)
            );

            broadcastToast({ text: DELETE_SUCCESS, type: 'success' });
        },
        [DELETE_ERROR, DELETE_SUCCESS, broadcastToast, deleteAircraft]
    );

    return {
        aircraft,
        handleDelete,
        handleLoadMore,
        hasNextPage,
        isFetchingMore,
        isLoading
    };
};

export default useAircraftTable;
